import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { ScrollArea } from "@volca/ui/components/ui/scroll-area";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@volca/ui/components/ui/toggle-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { count } from "@volca/ui/trades";
import { dayjs } from "@/core/utils/date";
import type { RecordObject, RecordRef } from "@/profitability/records/objects";
import {
  flagColumn,
  recordId,
  recordName,
} from "@/profitability/records/objects";
import { RecordChip } from "@/profitability/records/record-chip";
import type { RecordList } from "@/profitability/records/record-panel";
import type { ReportRow } from "@/profitability/types";

export const PERIOD_BAR_HEIGHT = 96;
const PAGE_SIZE = 50;

/** A day's volume opens its saved records, with a separate flagged view. */
export const PeriodDay = ({
  date,
  byWeek,
  object,
  rows,
  height,
  onOpenRecord,
}: {
  date: dayjs.Dayjs;
  byWeek: boolean;
  object: RecordObject;
  rows: ReportRow[];
  height: number;
  onOpenRecord: (ref: RecordRef, list?: RecordList | null) => void;
}) => {
  const [open, setOpen] = useState(false);
  const openingRecord = useRef(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const flag = flagColumn(object.dataset);
  const flagged = rows.filter((row) => flag && row[flag.key]);
  const shown = filter === "flagged" ? flagged : rows;
  const start = page * PAGE_SIZE;
  const visible = shown.slice(start, start + PAGE_SIZE);
  const noun = object.name.toLowerCase();
  const plural = object.plural.toLowerCase();
  const label = byWeek
    ? `Week of ${date.format("MMM D")}`
    : date.format("dddd, MMM D");
  const summary = `${count(rows.length, noun, plural)}, ${flagged.length} flagged`;
  const list = {
    label: `${label}, ${filter === "flagged" ? "flagged " : ""}${plural}`,
    refs: shown.map((row) => ({
      dataset: object.dataset.id,
      recordId: recordId(object.dataset, row),
    })),
  };
  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          openingRecord.current = false;
          setFilter("all");
          setPage(0);
        }
      }}
    >
      <Tooltip disabled={open}>
        <TooltipTrigger
          render={
            <PopoverTrigger
              ref={trigger}
              onClick={(event) => {
                trigger.current = event.currentTarget;
              }}
              disabled={!rows.length}
              aria-label={`View ${label}: ${summary}`}
              render={
                <button
                  type="button"
                  className="grid min-h-11 w-full min-w-0 grid-cols-[3.5rem_minmax(0,1fr)_4rem] items-center gap-3 rounded-md px-2 py-2 text-start focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus enabled:hover:bg-neutral-subtle-hovered disabled:cursor-default data-popup-open:bg-neutral-subtle-pressed @md/week:flex @md/week:flex-col @md/week:gap-2 @md/week:px-0 @md/week:py-1"
                />
              }
            />
          }
        >
          <Stack
            direction="row"
            align="end"
            justify="center"
            aria-hidden
            className="order-1 hidden w-full @md/week:flex"
            style={{ height: PERIOD_BAR_HEIGHT }}
          >
            <Stack
              justify="end"
              className="w-3/5 max-w-6 overflow-hidden rounded-xs bg-line-strong"
              style={{ height: rows.length ? Math.max(height, 2) : 0 }}
            >
              {flagged.length > 0 && (
                <span
                  className="block bg-warning"
                  style={{
                    height: `${(flagged.length / rows.length) * 100}%`,
                    minHeight: 2,
                  }}
                />
              )}
            </Stack>
          </Stack>
          <span
            aria-hidden
            className="order-1 text-xs whitespace-nowrap tabular-nums @md/week:order-2 @md/week:border-t @md/week:border-line @md/week:pt-1.5"
          >
            <span className="font-medium text-ink">
              {date.format(byWeek ? "MMM" : "ddd")}
            </span>{" "}
            <span className="text-subtle">{date.format("D")}</span>
          </span>
          <span aria-hidden className="order-2 h-2 min-w-0 @md/week:hidden">
            <span
              className="flex h-full overflow-hidden rounded-xs bg-line-strong"
              style={{
                width: rows.length
                  ? `${(height / PERIOD_BAR_HEIGHT) * 100}%`
                  : 0,
              }}
            >
              {flagged.length > 0 && (
                <span
                  className="h-full bg-warning"
                  style={{
                    width: `${(flagged.length / rows.length) * 100}%`,
                    minWidth: 2,
                  }}
                />
              )}
            </span>
          </span>
          <Stack
            aria-hidden
            className="order-3 items-end text-xs leading-normal whitespace-nowrap tabular-nums @md/week:items-center"
          >
            <span className="text-subtle">{rows.length} total</span>
            <span className="text-warning">{flagged.length} flagged</span>
          </Stack>
        </TooltipTrigger>
        <TooltipContent>
          <Stack gap={1}>
            <span className="font-medium">{label}</span>
            <span>{summary}</span>
          </Stack>
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        className="w-80 max-w-[calc(100vw-2rem)] p-3"
        finalFocus={() => !openingRecord.current}
      >
        <PopoverTitle>{label}</PopoverTitle>
        <ToggleGroup
          aria-label="Records to show"
          value={[filter]}
          onValueChange={(value) => {
            if (value[0]) {
              setFilter(value[0]);
              setPage(0);
            }
          }}
          size="sm"
        >
          <ToggleGroupItem
            value="all"
            className="min-h-10 pointer-coarse:min-h-11"
          >
            All {rows.length}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="flagged"
            className="min-h-10 pointer-coarse:min-h-11"
          >
            Flagged {flagged.length}
          </ToggleGroupItem>
        </ToggleGroup>
        <ScrollArea
          key={`${filter}-${page}`}
          className="h-64 max-h-[calc(100svh-16rem)]"
          viewportProps={{ "aria-label": list.label }}
        >
          {open &&
            (shown.length ? (
              <Stack render={<ul />} gap={1} className="pe-2">
                {visible.map((row, index) => (
                  <li key={list.refs[start + index].recordId}>
                    <button
                      type="button"
                      aria-label={`Open ${noun} ${recordName(object, row)}`}
                      className="flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-2 py-2 text-start hover:bg-neutral-subtle-hovered focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus"
                      onClick={() => {
                        openingRecord.current = true;
                        setOpen(false);
                        onOpenRecord(list.refs[start + index], {
                          ...list,
                          returnFocus: trigger.current,
                        });
                      }}
                    >
                      <RecordChip
                        object={object}
                        name={recordName(object, row)}
                        variant="plain"
                      />
                      {!!flag && !!row[flag.key] && (
                        <WarningIcon
                          aria-label="Flagged"
                          className="size-4 shrink-0 text-warning"
                        />
                      )}
                    </button>
                  </li>
                ))}
              </Stack>
            ) : (
              <p className="py-4 text-sm text-subtle">
                No flagged {plural} {byWeek ? "this week" : "on this day"}.
              </p>
            ))}
        </ScrollArea>
        {shown.length > PAGE_SIZE && (
          <Stack direction="row" align="center" justify="between" gap={2}>
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </Button>
            <span role="status" className="text-xs text-subtle tabular-nums">
              {start + 1}–{Math.min(start + PAGE_SIZE, shown.length)} of{" "}
              {shown.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11"
              disabled={start + PAGE_SIZE >= shown.length}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Stack>
        )}
      </PopoverContent>
    </Popover>
  );
};
